/**
 * 需求来源：docs/pod页面改版/AI提示词.md 第1次提交第3次补充(图片迁移 OSS) + 第4次补充(类目/商品接口化)。
 * 注意事项：
 *  1. 返回 SEO、cdnHost、publicImg、ossHost、categories；ossHost 见下，categories 为接口实时数据。
 *  2. ossHost 为动态 OSS 域名（dev/test=test-cj-frontend.oss-...，prod=sv-resource.oss-...），
 *     home 区块图片(jpg/svg/cta-bg)统一拼接 `${ossHost}/resources/printonDemand/home/xxx`，
 *     avatar/logo 用 OSS 雪碧图 CSS 类（见 constants.js / index.jsx）。
 *  3. ossHost 链路与 odm 一致：优先取 egg ctx.app.config.view.variable.OSS_HOST，CSR 回退全局/window；
 *     OSS_HOST 与 CDN_HOST 各环境取值一致(均为 ossHost[env])，故可互为兜底。
 *  4. ossHost/categories 在 SSR 阶段解析后随首屏 state 序列化下发，客户端 hydrate 直接复用。
 *  5. CATEGORY 模块（Explore Custom Products）类目+商品按用户决策走真实接口（覆盖此前硬编码决策）：
 *     - 类目：product-api/cj/individuationProduct/getIndividuationCategoryInfo（categoryId:''）
 *     - 商品：product-api/cj/individuationProduct/getIndividuationProductInfo（每类目第一页 10 个）
 *     用户决策「SSR 预取全部类目(并行)」，request(config)=>[err,body] 为框架注入的 SSR/CSR 通用请求函数。
 *
 * @param {Object} ctx - SSR 直传 Koa ctx 或 ssrDataLoader 统一上下文
 * @param {Function} request - 框架注入的统一请求函数 async (config) => [err, body]
 * @returns {Object} 页面初始化数据，含 __seo、cdnHost、publicImg、ossHost、categories
 */
async function fetchData(ctx, request) {
  let cdnHost = '';
  let publicImg = '';
  let ossHost = '';
  const eggCtx = (ctx && ctx.app) ? ctx : (ctx && ctx.ctx);
  if (eggCtx && eggCtx.app && eggCtx.app.config) {
    const viewVar = eggCtx.app.config.view && eggCtx.app.config.view.variable;
    cdnHost = (viewVar && viewVar.CDN_HOST) || '';
    publicImg = (viewVar && viewVar.PUBLIC_IMG) || '';
    ossHost = (viewVar && (viewVar.OSS_HOST || viewVar.CDN_HOST)) || '';
  }
  // CSR fallback：从 layout 模板注入的全局变量获取
  if (!cdnHost && typeof CDN_HOST !== 'undefined') {
    cdnHost = CDN_HOST;
  }
  if (!publicImg && typeof PUBLIC_IMG !== 'undefined') {
    publicImg = PUBLIC_IMG;
  }
  // ossHost CSR 兜底：window.OSS_HOST（产品行情等页注入）→ 全局 CDN_HOST（与 OSS_HOST 等值）
  if (!ossHost && typeof window !== 'undefined' && window.OSS_HOST) {
    ossHost = window.OSS_HOST;
  }
  if (!ossHost && typeof CDN_HOST !== 'undefined') {
    ossHost = CDN_HOST;
  }
  if (!publicImg) {
    publicImg = '/egg/image';
  }

  // 第1次提交第14次补充：中心 hub 展示站点 favicon，参考 layout 的 siteCheck.data('favicon')
  let favicon = '';
  try {
    if (eggCtx && eggCtx.siteCheck && typeof eggCtx.siteCheck.data === 'function') {
      favicon = eggCtx.siteCheck.data('favicon') || '';
    }
  } catch (e) { favicon = ''; }
  if (!favicon && typeof window !== 'undefined' && window.siteCheck && typeof window.siteCheck.data === 'function') {
    try { favicon = window.siteCheck.data('favicon') || ''; } catch (e) { /* ignore */ }
  }

  // 第1次提交第22次补充：中心 hub 改展示 `${ossHost}/config-resource/${siteName.toLowerCase()}/chat-logo.svg`，
  // siteName 取站点标识 siteCheck.data('name')（与 service/footerPages.js 的 config-resource 拼接范式一致），缺省回退 'cj'
  let siteName = '';
  try {
    if (eggCtx && eggCtx.siteCheck && typeof eggCtx.siteCheck.data === 'function') {
      siteName = eggCtx.siteCheck.data('name') || '';
    }
  } catch (e) { siteName = ''; }
  if (!siteName && typeof window !== 'undefined' && window.siteCheck && typeof window.siteCheck.data === 'function') {
    try { siteName = window.siteCheck.data('name') || ''; } catch (e) { /* ignore */ }
  }
  if (!siteName) { siteName = 'CJ'; }

  // 第1次提交第29次补充：文案中的 CJdropshipping/CJ 改为传入字段——
  // fullName 取 siteCheck.data('full-name')（如 CJdropshipping），缺省回退 'CJdropshipping'；
  // siteName(上方 data('name')，如 CJ) 复用为 {{name}}。constants.js 用其做 i18next 插值。
  let fullName = '';
  try {
    if (eggCtx && eggCtx.siteCheck && typeof eggCtx.siteCheck.data === 'function') {
      fullName = eggCtx.siteCheck.data('full-name') || '';
    }
  } catch (e) { fullName = ''; }
  if (!fullName && typeof window !== 'undefined' && window.siteCheck && typeof window.siteCheck.data === 'function') {
    try { fullName = window.siteCheck.data('full-name') || ''; } catch (e) { /* ignore */ }
  }
  if (!fullName) { fullName = 'CJdropshipping'; }

  // ===== CATEGORY 模块：接口拉取类目 + 各类目第一页 10 个商品（SSR 并行预取，失败兜底空数组，不阻断页面） =====
  var categories = [];
  if (typeof request === 'function') {
    try {
      var catRes = await request({
        url: 'product-api/cj/individuationProduct/getIndividuationCategoryInfo',
        method: 'post',
        data: { categoryId: '' },
        headers: { language: 'en' },
      });
      var catList = (catRes && catRes[1] && catRes[1].data) || [];
      categories = await Promise.all(
        catList.map(async function (cate) {
          var catId = cate.ID || cate.id || '';
          var catName = cate.NAME_EN || cate.nameEn || cate.name || '';
          var products = [];
          try {
            var prodRes = await request({
              url: 'product-api/cj/individuationProduct/getIndividuationProductInfo',
              method: 'post',
              data: { page: '1', size: '10', categoryId: catId, type: '1', productName: '' },
              headers: { language: 'en' },
            });
            var content = (prodRes && prodRes[1] && prodRes[1].data && prodRes[1].data.content) || [];
            products = content.slice(0, 10).map(function (p) {
              var img = p.bigImage || p.bigImg || '';
              if (img) img = 'https://' + String(img).replace('https://', '').replace('http://', '');
              return {
                id: p.id,
                name: p.nameEn || p.name || '',
                image: img,
                sellPrice: p.sellPrice || '',
                nowPrice: p.nowPrice || '',
              };
            });
          } catch (e) { /* 单类目商品失败兜底空，不影响其余类目 */ }
          return { id: catId, name: catName, products: products };
        })
      );
    } catch (e) {
      categories = [];
    }
  }

  return {
    cdnHost: cdnHost,
    publicImg: publicImg,
    ossHost: ossHost,
    favicon: favicon,
    siteName: siteName,
    fullName: fullName,
    categories: categories,
    // 第1次提交第35~36次补充：按用户提供的 SEO 文案更新 /printonDemand/home 的 Title/Description/Keywords；
    // 文案中的 CJdropshipping 改为传入字段 fullName(siteCheck.data('full-name')，缺省 CJdropshipping，见上方解析)。
    __seo: {
      title: `Print on Demand & Dropshipping Service | ${fullName} POD`,
      keywords: `Print on demand, POD dropshipping, print on demand dropshipping, ${fullName} POD`,
      description: `Create and sell custom products with ${fullName} POD service. Start print on demand dropshipping with easy product sourcing, fulfillment, and global shipping.`,
    },
  };
}

module.exports = fetchData;
